import { Design } from "../../core/models/entities/Design";
import { DesignStatus } from "../../core/models/entities/enums/DesignStatus";
import {
  Approval,
  ApprovalGroup,
  ApprovalStatus,
} from "../../core/models/entities/Approval";
import {
  BaseLogo,
  getLogoState,
  ImageLogo,
  LogoEditStatus,
  LogoState,
} from "../../core/models/entities/BaseLogo";
import { DataError } from "../../core/errors/DataError";
import { NotApprovedOrderItem } from "../../core/models/entities/ErrorStatus";
import {
  DESIGN_NOT_APPROVED_ERROR_CODE,
  LOGO_NOT_APPROVED_ERROR_CODE,
} from "../../data/models/mappers/mapError";
import { enviroment } from "../../enviroment";
import { anyOrderabilityGuard } from "./orderabilityScope";
import { isLogoNavWriteBackPending } from "./logoRejectAndEdit";
import { Role } from "../../core/models/entities/Role";

// Flag REACT_APP_DESIGN_ORDERABILITY_GUARD_ENABLED (Zendesk #724425, 15/10):
// the ONE rule for "may this design / logo be put in the cart and ordered",
// shared with the API guard OrderDesignApprovalGuardMode (API PR, same
// rule, fail-closed at PlaceOrder / AddProductsToOrder). Pure functions over
// the fields the list rows already carry - 0 API calls. The callers gate on
// the flag; these helpers do not, so they can be unit-tested as a table.
//
// The rule guards ORDERING only. The multi-select checkbox is never
// disabled by it (Nichlas 16/10, after the first prod day: «vi er stadig
// enige om at vi kan markere flere selvom de ikke er godkendte og sende
// korrektur ud») - the same checkbox drives Send via email (the external
// approval mail), Download PDF, multi-delete and folder drag, which must
// work on AWAITING rows. The guard sits in the bulk "Add to Order (n)"
// (filter + skip toast, disabled when nothing selected is orderable), the
// row / popup button and the API (warn -> enforce).
//
// Why "status done" alone is not enough for a design: the JS upload's
// CreateDesign stores a design as done + vendor Pending (JsUploadFunctions
// 4176-4179), and the "send to customer" deferral keeps approval with the
// vendor Approved + customer Pending - both show "AWAITING · Vendor /
// Customer" in the list and must not be orderable until the vote is in.

export { DESIGN_NOT_APPROVED_ERROR_CODE, LOGO_NOT_APPROVED_ERROR_CODE };

// i18n keys (all 10 locale files).
export const DESIGNS_SKIPPED_NOT_APPROVED_KEY =
  "designListStyle.skippedNotApproved";
export const LOGOS_SKIPPED_NOT_APPROVED_KEY = "logoList.skippedNotApproved";
// Hint on the DISABLED bulk "Add to Order (n)" button when nothing selected
// is orderable (Nichlas 16/10: the checkbox itself must stay free, so the
// dealer can tick AWAITING rows and send a proof to several at once).
export const DESIGNS_NONE_ORDERABLE_KEY = "designListStyle.selectedNotApproved";
export const LOGOS_NONE_ORDERABLE_KEY = "logoList.selectedNotApproved";
// Bulk "Add to Order (n)" skipped logos whose supplier write-back is still
// pending (flag REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED, B3 Nichlas 17/10:
// no ordering while the NAV fix is pending - the price is the old one).
export const LOGOS_SKIPPED_NAV_WRITE_BACK_PENDING_KEY =
  "logoList.skippedNavWriteBackPending";
export const NOT_APPROVED_NOT_ORDERED_KEY = "orderCreate.notApprovedNotOrdered";
// Same sentence without the ": {{items}}" list, for the edge where the 400
// body named no items and carried no message (never «…ordered: –»).
export const NOT_APPROVED_NOT_ORDERED_GENERIC_KEY =
  "orderCreate.notApprovedNotOrderedGeneric";
// The toast is the only explanation the user gets for a refused order and
// it lists MULs - keep it on screen longer than the default 5 s.
export const NOT_APPROVED_TOAST_MS = 10_000;

function isBlocking(participant: Approval | null | undefined): boolean {
  if (!participant) return false;
  return (
    participant.status === ApprovalStatus.Pending ||
    participant.status === ApprovalStatus.Rejected
  );
}

/** True when no participant of the group is Pending or Rejected (null group = nobody asked = ok). */
export function approvalGroupSettled(
  approval: ApprovalGroup | null | undefined,
): boolean {
  if (!approval) return true;
  return (
    !isBlocking(approval.vendor) &&
    !isBlocking(approval.customer) &&
    !isBlocking(approval.external)
  );
}

/**
 * True when a participant of the group (vendor / customer / external) still
 * has to vote (status Pending). Flag REACT_APP_DESIGN_STATUS_FOLLOWS_APPROVAL_
 * ENABLED: the design list's Status cell shows AWAITING for exactly these
 * rows, whatever design.status says (JS upload = done + vendor Pending,
 * deferred customer approval = approval + customer Pending). Rejected votes
 * are not "pending" - the rejected row keeps its own rendering. Null group =
 * nobody asked = false. Callers gate on the flag.
 */
export function approvalHasPendingParticipant(
  approval: ApprovalGroup | null | undefined,
): boolean {
  if (!approval) return false;
  return [approval.vendor, approval.customer, approval.external].some(
    (participant) => participant?.status === ApprovalStatus.Pending,
  );
}

/**
 * Rule §1 for designs: status done AND no participant (vendor / customer /
 * external) Pending or Rejected. Draft, approval and rejected designs are
 * never orderable; a done design with a pending vote (JS upload, deferred
 * customer approval) is not either.
 */
export function isDesignOrderable(design: Design): boolean {
  if (!design) return false;
  if (design.status !== DesignStatus.Done) return false;
  return approvalGroupSettled(design.approval);
}

/**
 * Flag REACT_APP_VENDOR_ORDER_WHILE_CUSTOMER_PENDING_ENABLED (Nichlas 18/10,
 * MUL352937): the ONE form the dealer may settle and order from the list
 * although isDesignOrderable says no - the dealer's own vote is in, only the
 * customer's is still Pending. Status Approval (done + customer Pending is
 * the API's FORM 3, out of scope: skip on done is refused), vendor Approved,
 * customer Pending (Rejected = never, Approved = the plain rule decides),
 * external absent or Approved, viewer = Vendor. Pure, 0 calls, no flag read:
 * the row gates on the flag and the click settles the group (approval-start
 * {skip:true}) BEFORE the cart, so the API guard never sees a pending vote.
 * Whether the design has a customer org is irrelevant (a design without a
 * customer whose customer vote hangs is the same form).
 */
export function vendorCanSettleAndOrder(
  design: Design,
  role: Role | number | null | undefined,
): boolean {
  if (!design) return false;
  if (role !== Role.Vendor) return false;
  if (design.status !== DesignStatus.Approval) return false;
  const approval = design.approval;
  if (!approval) return false;
  if (approval.vendor?.status !== ApprovalStatus.Approved) return false;
  if (approval.customer?.status !== ApprovalStatus.Pending) return false;
  return (
    !approval.external || approval.external.status === ApprovalStatus.Approved
  );
}

/**
 * Flag REACT_APP_LOGO_VENDOR_APPROVED_ORDERABLE_ENABLED (19/10, prod
 * 05:56-06:51Z: 12 PlaceOrder refusals «kind=logo … reason=validating
 * outcome=blocked» at 4 customer orgs): the ONE form the flag opens - an
 * image logo in status Validating whose vendor vote is Approved and where no
 * participant (vendor / customer / external) is Rejected. That is a logo the
 * dealer approved and sent to the customer (approval-start skip=false),
 * where the customer never answered, so the status never reached Done;
 * before the guard it was orderable. Pending votes (customer / external) do
 * not block - the dealer's word is enough, for the dealer and the customer
 * alike. Validating without the dealer's Approved vote, Draft, Rejected,
 * Done (rule §1 orders it anyway) and rows without a status field are NOT
 * this form. Pure, 0 calls, no flag read: isLogoOrderable gates on the flag.
 */
export function isLogoVendorApprovedOrderable(
  logo: BaseLogo | null | undefined,
): boolean {
  if (!logo) return false;
  if ((logo as ImageLogo).status !== LogoEditStatus.Validating) return false;
  const approval = logo.approval;
  if (approval?.vendor?.status !== ApprovalStatus.Approved) return false;
  return (
    approval.customer?.status !== ApprovalStatus.Rejected &&
    approval.external?.status !== ApprovalStatus.Rejected
  );
}

/**
 * Rule §1 for logos: only status done is orderable - draft, validating and
 * rejected are refused (the API's LogoExceptionThrower lets exactly those
 * three through to CommitLogo, which is the defect the guard closes). Rows
 * without a status field (text / name / number / symbol logos) fall back to
 * the row button's own rule, getLogoState === Approved, so nothing that is
 * orderable from the row becomes un-orderable in bulk.
 */
export function isLogoOrderable(logo: BaseLogo): boolean {
  if (!logo) return false;
  // "Sending to supplier" (flag logoRejectAndEditNavEnabled, B3): a fixed
  // NAV logo is status done again, but its price is the OLD one until the
  // API's worker has written the changes to NAV and re-imported the item -
  // not orderable meanwhile, in bulk, from the row and from the popup
  // alike. Always false with that flag off = today's rule.
  if (isLogoNavWriteBackPending(logo)) return false;
  const status = (logo as ImageLogo).status;
  if (typeof status === "string") {
    // Flag REACT_APP_LOGO_VENDOR_APPROVED_ORDERABLE_ENABLED (19/10): a
    // Validating logo the dealer approved, nobody Rejected - the customer
    // simply never answered - is orderable (the API guard's own flag opens
    // the same form). Always false with the flag off = today's line below.
    if (
      enviroment.logoVendorApprovedOrderableEnabled &&
      isLogoVendorApprovedOrderable(logo)
    ) {
      return true;
    }
    return status === LogoEditStatus.Done;
  }
  return getLogoState(logo) === LogoState.Approved;
}

/**
 * Logo ROW button (logo-list-item): getLogoState calls a Validating image
 * logo whose votes are all Approved "Approved" and shows Stepper + "Add to
 * order" - but rule §1 (status done only) refuses it, so the API would 400
 * at checkout. True = the row must not offer "Add to order" (it shows
 * "View designs" instead). Callers gate on the flag.
 */
export function isLogoRowAddToOrderBlocked(logo: BaseLogo): boolean {
  if (!logo) return false;
  return getLogoState(logo) === LogoState.Approved && !isLogoOrderable(logo);
}

/**
 * The logo ROW's one answer for "no Stepper / Add to order" (it shows "View
 * designs" instead): the orderability guard when it is active for the
 * viewer, or the pending supplier write-back of a fixed NAV logo under ITS
 * flag (B3). `guardActive` is the row's viewer-aware answer
 * (useOrderabilityGuardActive: the flag OR the viewer's organisation / its
 * parent in the org-id list); its default is the flag itself, so every
 * caller that passes nothing keeps today's rule. Both off = today's row.
 */
export function isLogoRowOrderBlocked(
  logo: BaseLogo,
  guardActive: boolean = enviroment.designOrderabilityGuardEnabled,
): boolean {
  if (!logo) return false;
  if (guardActive && isLogoRowAddToOrderBlocked(logo)) {
    return true;
  }
  return isLogoNavWriteBackPending(logo) === true;
}

export interface OrderableSplit<T> {
  orderable: T[];
  skipped: T[];
}

export function splitOrderableDesigns(designs: Design[]): OrderableSplit<Design> {
  const orderable: Design[] = [];
  const skipped: Design[] = [];
  for (const d of designs) (isDesignOrderable(d) ? orderable : skipped).push(d);
  return { orderable, skipped };
}

export function splitOrderableLogos(logos: BaseLogo[]): OrderableSplit<BaseLogo> {
  const orderable: BaseLogo[] = [];
  const skipped: BaseLogo[] = [];
  for (const l of logos) (isLogoOrderable(l) ? orderable : skipped).push(l);
  return { orderable, skipped };
}

/**
 * B3 (flag REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED): the part of a bulk
 * selection whose supplier write-back is still pending goes to `skipped`,
 * the rest to `orderable`, order kept. Independent of the orderability
 * guard's flag (off in prod), so bulk "Add to Order (n)" never puts a
 * pending NAV logo in the cart at the old price. Flag off = nothing skipped.
 */
export function splitNavWriteBackPendingLogos(
  logos: BaseLogo[],
): OrderableSplit<BaseLogo> {
  const orderable: BaseLogo[] = [];
  const skipped: BaseLogo[] = [];
  for (const l of logos)
    (isLogoNavWriteBackPending(l) ? skipped : orderable).push(l);
  return { orderable, skipped };
}

/**
 * True when there IS a selection and not one of its designs is orderable:
 * the bulk "Add to Order (n)" button is then disabled with a hint instead
 * of a click that would only skip everything. Empty selection = false (the
 * button has its own "select first" rule). Callers gate on the flag.
 */
export function noneOrderableDesigns(designs: Design[]): boolean {
  return designs.length > 0 && !designs.some(isDesignOrderable);
}

/** Logo twin of noneOrderableDesigns (rule §1 for logos: status done only). */
export function noneOrderableLogos(logos: BaseLogo[]): boolean {
  return logos.length > 0 && !logos.some(isLogoOrderable);
}

/**
 * The API guard's refusal (mode enforce): 400 { code: "designNotApproved" |
 * "logoNotApproved", items: [{ mul, title, designId | logoId, reason }] } at
 * PlaceOrder / CreateOrder / CommitOrder. errorStatusFromBody keeps the
 * marker as errorCode and the items as notApprovedItems. Gated on
 * anyOrderabilityGuard() (the flag OR a non-empty org-id list - the API
 * sends the marker only where it enforces). Flag off + empty list = always
 * false, whatever the error = today's compound toast.
 */
export function isNotApprovedOrderError(error: unknown): boolean {
  if (!anyOrderabilityGuard()) return false;
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 400) return false;
  return (
    status.errorCode === DESIGN_NOT_APPROVED_ERROR_CODE ||
    status.errorCode === LOGO_NOT_APPROVED_ERROR_CODE
  );
}

/** The items the 400 named, de-duplicated by MUL / product no / id; [] when the body carried none. */
export function notApprovedOrderItems(error: unknown): NotApprovedOrderItem[] {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const items = status?.notApprovedItems ?? [];
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = itemLabel(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// The label the user can find the line by: the design's MUL, else the
// item's product number (the API sets `mul` only for designs and
// `productNo` = Product.Sku ?? Serial for logos - the internal logo id is
// shown nowhere in the UI), else the id as a last resort.
function itemLabel(item: NotApprovedOrderItem): string {
  if (item.mul) return item.mul;
  if (item.productNo) return item.productNo;
  if (item.designId != null) return `design ${item.designId}`;
  if (item.logoId != null) return `logo ${item.logoId}`;
  return "";
}

/**
 * ONE readable line for the toast: «These items are awaiting approval and
 * were not added or ordered: MUL352091 "Højen G6U", MUL352092 "…"». The
 * API's own English `message` is the fallback when it named no items.
 */
export function notApprovedOrderToastText(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const items = notApprovedOrderItems(error).map((item) => {
    const label = itemLabel(item);
    return item.title ? `${label} "${item.title}"` : label;
  });
  if (items.length === 0) {
    const status = error instanceof DataError ? error.errorStatus : undefined;
    const message = status?.message;
    if (typeof message === "string" && message) {
      return t(NOT_APPROVED_NOT_ORDERED_KEY, { items: message });
    }
    return t(NOT_APPROVED_NOT_ORDERED_GENERIC_KEY);
  }
  return t(NOT_APPROVED_NOT_ORDERED_KEY, { items: items.join(", ") });
}
